/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-22-09 00:00:00
 *
 *******************************************************/
/// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, getFormValues, change } from 'redux-form';
import { FormLayout, TextStyle, Link } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderAutocomplete } from 'src/components/views/common/autocomplete-hoc';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import {
  FILTER_OPTION_DISPLAY_TYPE_LOCATION,
  FILTER_OPTION_SELECT_TYPES,
  FILTER_OPTION_INVENTORY_STATUS,
  getFilterOptionValueTypes
} from 'src/constants/filter-tree';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { URL } from 'src/constants/links';

type Props = WithI18nProps;

class Location extends Component<Props> {
  componentDidMount() {
    const { locations, locationOptionValues } = this.props;

    if (locationOptionValues === 'available_sale_online') {
      const nonFulfillment = _.filter(locations, { fulfillsOnlineOrders: false });
      this.props.updateField('locationNonFulfillmentValues', nonFulfillment);
    } else {
      this.props.updateField('locationNonFulfillmentValues', []);
    }
  }

  render() {
    const {
      i18n,
      optionType,
      locationInventoryStatus,
      locationOptionValues,
      locationForceDisplayInventoryBaseSelectedLocations,
      locations,
      supportLocationNonFulfillsOnline = true,
      appLanguage
    } = this.props;

    const optionInventoryStatus = FILTER_OPTION_INVENTORY_STATUS.map(function (type) {
      return {
        ...type,
        label: i18n.translate(type.label)
      };
    });

    const displayTypes = FILTER_OPTION_DISPLAY_TYPE_LOCATION.map(function (type) {
      return {
        ...type,
        label: i18n.translate(type.label)
      };
    });
    const optionSelect = FILTER_OPTION_SELECT_TYPES.map(function (type) {
      return {
        ...type,
        label: i18n.translate(type.label)
      };
    });

    let optionValues = getFilterOptionValueTypes(optionType, appLanguage, i18n);
    let optionManualValues = locations;
    const optionExcludedValues = this.getExcludedValues();

    if (!supportLocationNonFulfillsOnline) {
      // Remove option Location with available inventory for online store
      _.remove(optionValues, { value: 'all' });
      // Rename to "All"
      optionValues.forEach(option => {
        if (option.value === 'available_sale_online') {
          option.label = i18n.translate(
            'AdminHTML.FilterTree.Constant.getFilterOptionValueTypes_All_Locations'
          );
        }
      });

      // Filter online store for manual values
      optionManualValues = optionManualValues.filter(location => location.fulfillsOnlineOrders);
    }

    return (
      <FormLayout>
        <Field
          name="locationInventoryStatus"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Field_InventoryStatus'
          )}
          options={optionInventoryStatus}
          onChange={(e, value) => this.changeInventoryStatus(value)}
          tooltip={
            <span>
              <div>
                {i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Field_InventoryStatus_Tooltip_1',
                  {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Field_InventoryStatus_Tooltip_1_t1'
                        )}
                      </TextStyle>
                    )
                  }
                )}
              </div>
              <div>
                {i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Field_InventoryStatus_Tooltip_2',
                  {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Field_InventoryStatus_Tooltip_2_t1'
                        )}
                      </TextStyle>
                    ),
                    t2: (
                      <Link external url={URL['LocationByInventory']}>
                        {i18n.translate('AdminHTML.Common.Action.learnMore')}
                      </Link>
                    )
                  }
                )}
              </div>
            </span>
          }
        />

        <Field
          name="locationDisplayType"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionDisplay_Label'
          )}
          options={displayTypes}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionDisplay_Tooltip'
          )}
        />

        <Field
          name="locationOptionSelect"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionSelect_Label'
          )}
          options={optionSelect}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionSelect_Tooltip'
          )}
        />

        <Field
          name="locationOptionValues"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionValues_Label'
          )}
          options={optionValues}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionValues_Tooltip'
          )}
          onChange={(e, value) => this.changeOptionValue(value)}
        />

        {locationOptionValues === 'specific' ? (
          <Field
            name="locationManualValues"
            component={RenderAutocomplete}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_ManualValues_Label'
            )}
            placeholder={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_ProductType_Placeholder'
            )}
            options={optionManualValues}
          />
        ) : (
          <Field
            name="locationExcludedValues"
            component={RenderAutocomplete}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_ExcludedValues_Label'
            )}
            options={optionExcludedValues}
            placeholder={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_ProductType_Placeholder'
            )}
            tooltip={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Field_ExcludedValues_Tooltip'
            )}
          />
        )}

        {locationInventoryStatus === 'product_quantity' &&
          locationForceDisplayInventoryBaseSelectedLocations && (
            <Field
              name={`locationInventoryBaseSelectedLocations`}
              component={RenderToggleSwitch}
              label={i18n.translate(
                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Field_ShowProductStockStatus'
              )}
              tooltip={
                <span>
                  <div>
                    {i18n.translate(
                      'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Field_ShowProductStockStatus_Tooltip_1',
                      {
                        t1: (
                          <TextStyle variation="strong">
                            {i18n.translate(
                              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Field_ShowProductStockStatus_Tooltip_1_t1'
                            )}
                          </TextStyle>
                        ),
                        t2: (
                          <TextStyle variation="strong">
                            {i18n.translate(
                              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Field_ShowProductStockStatus_Tooltip_1_t2'
                            )}
                          </TextStyle>
                        )
                      }
                    )}
                  </div>
                  <div>
                    {i18n.translate(
                      'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Field_ShowProductStockStatus_Tooltip_2',
                      {
                        t1: (
                          <TextStyle variation="strong">
                            {i18n.translate(
                              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Field_ShowProductStockStatus_Tooltip_2_t1'
                            )}
                          </TextStyle>
                        ),
                        t2: (
                          <TextStyle variation="strong">
                            {i18n.translate(
                              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Field_ShowProductStockStatus_Tooltip_2_t2'
                            )}
                          </TextStyle>
                        ),
                        t3: (
                          <Link external url={URL['LocationByInventory']}>
                            {i18n.translate('AdminHTML.Common.Action.learnMore')}
                          </Link>
                        )
                      }
                    )}
                  </div>
                </span>
              }
            />
          )}
      </FormLayout>
    );
  }

  changeInventoryStatus = value => {
    const { locationForceDisplayInventoryBaseSelectedLocations } = this.props;

    if (value === 'variant_quantity') {
      this.props.updateField('locationInventoryBaseSelectedLocations', true);
    }

    if (value === 'product_quantity' && !locationForceDisplayInventoryBaseSelectedLocations) {
      this.props.updateField('locationInventoryBaseSelectedLocations', false);
    }
  };

  changeOptionValue = value => {
    const { locations } = this.props;

    // Auto update field nonFulfillment
    if (value === 'available_sale_online') {
      const nonFulfillment = _.filter(locations, { fulfillsOnlineOrders: false });
      this.props.updateField('locationNonFulfillmentValues', nonFulfillment);
    } else {
      this.props.updateField('locationNonFulfillmentValues', []);
    }
  };

  getExcludedValues = () => {
    const { locationOptionValues, locations = [] } = this.props;

    // Return all locations
    if (locationOptionValues !== 'available_sale_online') {
      return locations;
    }

    // Case available sale online
    let excludedOptions = locations.filter(location => location.fulfillsOnlineOrders);

    return excludedOptions;
  };
}

const mapStateToProps = (state, props) => {
  const formFilterOptionValues = getFormValues('OptionModalForm')(state);

  return {
    locations: _.get(state.filterData.filterData, 'locations', []),
    optionType: _.get(formFilterOptionValues, 'optionType', ''),
    locationInventoryStatus: _.get(
      formFilterOptionValues,
      'locationInventoryStatus',
      'product_quantity' // Default value for old store
    ),
    locationOptionValues: _.get(formFilterOptionValues, 'locationOptionValues', []),
    locationInventoryBaseSelectedLocations: _.get(
      formFilterOptionValues,
      'locationInventoryBaseSelectedLocations',
      false
    ),
    locationForceDisplayInventoryBaseSelectedLocations: _.get(
      formFilterOptionValues,
      'locationForceDisplayInventoryBaseSelectedLocations',
      false
    ),
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE),
    supportLocationNonFulfillsOnline: _.get(
      state.oauth.tenantApp,
      'parseParams.supportLocationNonFulfillsOnline',
      true
    )
  };
};

const mapDispatchToProps = dispatch => ({
  updateField: (field, value) => dispatch(change(`OptionModalForm`, field, value))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(Location));
